import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppChip, { AppStatusChip } from "../../design-system/components/AppChip";
import AppText from "../../design-system/components/AppText";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  IconChevronDown,
  IconInfo,
  IconLock,
  IconTrendingUp,
} from "../../design-system/icons";
import DemandNumberField from "./DemandNumberField";
import DemandOccupancyDialog from "./DemandOccupancyDialog";
import { defaultKey, demandKey, shiftBreakLabel } from "./demandModel";
import OccupancyChart from "./OccupancyChart";
import { buildOccupancyCurve, occupancyStatus, OCCUPANCY_STATUS } from "./occupancyModel";

const DemandDepartmentCard = ({
  department,
  dates,
  mode = "week",
  values = {},
  defaultValues = {},
  overrides = {},
  expanded,
  onToggle,
  onUpdateCell,
  onUpdateDefaultCell,
}) => {
  const { t } = useTranslation();
  const departmentColor = department.color_hex || "var(--ds-color-brand-default)";
  const isDefaultView = mode === "default";
  const sourceValues = isDefaultView ? defaultValues : values;
  const [occupancyDay, setOccupancyDay] = useState(null);

  // Kopfzahl je Schicht fuer einen Tag, nach demselben Schema wie die
  // Bedarfszellen selbst (demandKey im Wochen-, defaultKey im Standard-Modus)
  // — Grundlage sowohl fuer die Mini-Kurve in der Zeile als auch fuer den
  // grossen Dialog, damit beide garantiert dieselben Zahlen zeigen.
  const shiftEntriesForDay = (day) => (department.shifts || []).map((shift) => {
    const key = isDefaultView
      ? defaultKey(department.id, shift.id, day.weekday_index)
      : demandKey(department.id, shift.id, day.date);
    return { shift, count: Number(sourceValues[key] || 0) };
  });
  const departmentTotal = (department.shifts || []).reduce((total, shift) => (
    total + dates.reduce((dayTotal, day) => {
      if (day.is_open === false) return dayTotal;
      const key = isDefaultView
        ? defaultKey(department.id, shift.id, day.weekday_index)
        : demandKey(department.id, shift.id, day.date);
      return dayTotal + Number(sourceValues[key] || 0);
    }, 0)
  ), 0);
  const adjustedCellCount = (department.shifts || []).reduce((count, shift) => (
    count + dates.reduce((dayCount, day) => {
      if (day.is_open === false) return dayCount;
      const currentValue = Number(values[demandKey(department.id, shift.id, day.date)] || 0);
      const standardValue = Number(
        defaultValues[defaultKey(department.id, shift.id, day.weekday_index)] || 0
      );
      return dayCount + (currentValue !== standardValue ? 1 : 0);
    }, 0)
  ), 0);

  return (
    <AppCard
      className="demand-v2__department-card"
      style={{ "--demand-department-accent": departmentColor }}
    >
      <button
        type="button"
        className="demand-v2__department-header"
        aria-expanded={expanded}
        aria-label={t(expanded ? "demand_ui.collapse_department" : "demand_ui.expand_department", {
          name: department.name,
        })}
        onClick={onToggle}
      >
        <span className="demand-v2__department-main">
          <span className="demand-v2__department-accent" aria-hidden="true" />
          <span className="demand-v2__department-title-row">
            <AppText as="strong" variant="heading-5" truncate>{department.name}</AppText>
            <AppChip size="sm" variant="primary" appearance="outlined" className="demand-v2__accent-chip">
              {department.shorthand_symbol || t("demand_ui.no_shorthand")}
            </AppChip>
          </span>
        </span>

        <span className="demand-v2__department-meta">
          <AppText variant="body-2" tone="secondary">
            {t(isDefaultView ? "demand_ui.department_default_total" : "demand_ui.department_total", {
              count: departmentTotal,
            })}
          </AppText>
          {!isDefaultView ? (
            <AppStatusChip
              status={adjustedCellCount > 0 ? "warning" : "success"}
              size="sm"
              showIcon={adjustedCellCount > 0}
            >
              {t(adjustedCellCount > 0 ? "demand_ui.differs_from_default" : "demand_ui.matches_default")}
            </AppStatusChip>
          ) : null}
          <IconChevronDown className={`demand-v2__department-chevron${expanded ? " is-expanded" : ""}`} />
        </span>
      </button>

      {expanded ? (
        <AppCardBody inset="none" className="demand-v2__department-body">
          {(department.shifts || []).length === 0 ? (
            <div className="demand-v2__department-empty">
              <AppCallout
                variant="info"
                description={t("demand_ui.department_without_shifts")}
              />
            </div>
          ) : (
            <div className="demand-v2__grid-scroll">
              <div className="demand-v2__grid">
                <div className="demand-v2__grid-corner" aria-hidden="true" />
                {dates.map((day) => (
                  <div
                    key={`${department.id}-head-${day.date}`}
                    className={`demand-v2__day-header${day.is_open === false ? " is-closed" : ""}`}
                  >
                    <AppText as="strong" variant="label-2">{day.weekday}</AppText>
                    <span className="demand-v2__day-meta">
                      {day.is_open === false ? <IconLock aria-hidden="true" /> : null}
                      <AppText variant="label-3" tone="tertiary">
                        {isDefaultView ? t("demand_ui.every_week") : day.label}
                      </AppText>
                    </span>
                  </div>
                ))}

                {(department.shifts || []).map((shift) => {
                  const shiftColor = shift.color_hex || departmentColor;
                  const rowTotal = dates.reduce((sum, day) => {
                    if (day.is_open === false) return sum;
                    const key = isDefaultView
                      ? defaultKey(department.id, shift.id, day.weekday_index)
                      : demandKey(department.id, shift.id, day.date);
                    return sum + Number(sourceValues[key] || 0);
                  }, 0);

                  return (
                    <React.Fragment key={`${department.id}-${shift.id}`}>
                      <div className="demand-v2__shift" style={{ "--demand-shift-accent": shiftColor }}>
                        <div className="demand-v2__shift-copy">
                          <span className="demand-v2__shift-title">
                            <AppText as="strong" variant="label-1" truncate>{shift.name}</AppText>
                            <AppTooltip
                              content={(
                                <span className="demand-v2__shift-tooltip">
                                  <strong>{shift.name}</strong>
                                  <span>{shift.start_time} - {shift.end_time}</span>
                                  <span>{shiftBreakLabel(shift, t)}</span>
                                </span>
                              )}
                            >
                              <span
                                className="demand-v2__shift-info"
                                tabIndex={0}
                                aria-label={t("demand_ui.shift_details", { name: shift.name })}
                              >
                                <IconInfo />
                              </span>
                            </AppTooltip>
                          </span>
                          <AppText variant="body-2" tone="secondary" truncate>
                            {shift.start_time} - {shift.end_time}
                          </AppText>
                          {isDefaultView ? (
                            <AppText variant="label-3" tone="tertiary">
                              {t("demand_ui.shift_week_total", { count: rowTotal })}
                            </AppText>
                          ) : null}
                        </div>
                        <AppChip size="sm" variant="neutral" appearance="semiFilled" className="demand-v2__shift-chip">
                          {shift.shorthand_symbol}
                        </AppChip>
                      </div>

                      {dates.map((day) => {
                        const key = isDefaultView
                          ? defaultKey(department.id, shift.id, day.weekday_index)
                          : demandKey(department.id, shift.id, day.date);
                        const baselineKey = defaultKey(
                          department.id,
                          shift.id,
                          day.weekday_index
                        );
                        const cellValue = sourceValues[key] ?? "";
                        const defaultValue = Number(defaultValues[baselineKey] || 0);
                        const currentValue = Number(cellValue || 0);
                        const hasOverride = (
                          !isDefaultView
                          && Object.prototype.hasOwnProperty.call(overrides, key)
                          && currentValue !== defaultValue
                        );
                        const delta = currentValue - defaultValue;
                        const isClosed = day.is_open === false;

                        return (
                          <div
                            key={key}
                            className={[
                              "demand-v2__cell",
                              isClosed ? "is-closed" : "",
                              hasOverride ? "has-override" : "",
                            ].filter(Boolean).join(" ")}
                          >
                            <AppText className="demand-v2__mobile-day" variant="label-3" tone="tertiary">
                              {day.weekday} {isDefaultView ? "" : day.label}
                            </AppText>
                            {isClosed ? (
                              <span
                                className="demand-v2__closed-cell"
                                aria-label={t("demand_ui.cell_closed", {
                                  department: department.name,
                                  shift: shift.name,
                                  day: day.weekday,
                                })}
                              >
                                <IconLock />
                              </span>
                            ) : (
                              <DemandNumberField
                                value={cellValue}
                                ariaLabel={t("demand_ui.cell_label", {
                                  department: department.name,
                                  shift: shift.name,
                                  day: day.weekday,
                                })}
                                onValueChange={(nextValue) => {
                                  if (isDefaultView) {
                                    onUpdateDefaultCell(
                                      department.id,
                                      shift.id,
                                      day,
                                      nextValue
                                    );
                                  } else {
                                    onUpdateCell(department.id, shift.id, day, nextValue);
                                  }
                                }}
                              />
                            )}
                            {hasOverride ? (
                              <AppText className="demand-v2__delta" variant="label-3">
                                {delta > 0 ? `+${delta}` : delta}
                              </AppText>
                            ) : null}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                <div className="demand-v2__occupancy-row">
                  <div className="demand-v2__occupancy-copy">
                    <span className="demand-v2__occupancy-title">
                      <IconTrendingUp aria-hidden="true" />
                      <AppText as="strong" variant="label-1">
                        {t("demand_ui.occupancy_row_title")}
                      </AppText>
                    </span>
                    <AppText variant="body-2" tone="secondary">
                      {t("demand_ui.occupancy_row_subtitle")}
                    </AppText>
                  </div>
                </div>

                {dates.map((day) => {
                  const entries = shiftEntriesForDay(day);
                  const status = occupancyStatus(day.is_open, entries);

                  return (
                    <div
                      key={`occupancy-${department.id}-${day.date}`}
                      className={[
                        "demand-v2__occupancy-cell",
                        status !== OCCUPANCY_STATUS.POPULATED ? "is-inactive" : "",
                      ].filter(Boolean).join(" ")}
                    >
                      <AppText className="demand-v2__mobile-day" variant="label-3" tone="tertiary">
                        {day.weekday} {isDefaultView ? "" : day.label}
                      </AppText>
                      {status === OCCUPANCY_STATUS.CLOSED ? (
                        <AppText variant="label-3" tone="tertiary">
                          {t("demand_ui.occupancy_closed")}
                        </AppText>
                      ) : status === OCCUPANCY_STATUS.EMPTY ? (
                        <AppText variant="label-3" tone="tertiary">
                          {t("demand_ui.occupancy_no_demand")}
                        </AppText>
                      ) : (
                        <button
                          type="button"
                          className="demand-v2__occupancy-trigger"
                          onClick={() => setOccupancyDay(day)}
                          aria-label={t("demand_ui.occupancy_open_detail", {
                            department: department.name,
                            day: isDefaultView ? day.weekday : `${day.weekday} ${day.label}`,
                          })}
                        >
                          <OccupancyChart curve={buildOccupancyCurve(entries)} variant="mini" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </AppCardBody>
      ) : null}

      <DemandOccupancyDialog
        open={Boolean(occupancyDay)}
        onClose={() => setOccupancyDay(null)}
        day={occupancyDay}
        isDefaultView={isDefaultView}
        shiftEntries={occupancyDay ? shiftEntriesForDay(occupancyDay) : []}
      />
    </AppCard>
  );
};

export default DemandDepartmentCard;
